import React, { useState } from 'react';
import { FileText, Upload, Trash2, Eye, FileBadge, Car, Globe, X } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { uploadToStorage, uploadBase64ToStorage } from '../../../config/supabase';
import { sendTelegramNotification } from '../../../utils/telegram';
import { buildTelegramMessage } from '../../../utils/telegramMessages';

import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';
import NationalityPicker from './NationalityPicker';
import LiveCameraCapture from '../../shared/components/LiveCameraCapture';
import DocumentViewerModal from '../../shared/components/DocumentViewerModal';

export default function ClientForm({ client, onSave, onClose, settings = {} }) {
  const { t, lang } = useLanguage();
  const isAr = lang === 'ar';

  const [data, setData] = useState(client || { 
    name:'', cin:'', phone:'', permis:'', address:'', email:'', notes:'', creditBalance: 0,
    nationality: 'Marocaine',
    birthDate: '',
    profession: '',
    passport: '',
    passportPlace: '',
    permisDate: '',
    phone2: '',  
    address2: '', 
    docCniRecto: null, docCniVerso: null,
    docPermisRecto: null, docPermisVerso: null,
    docPassport: null,
    docCni: null, docPermis: null
  });

  const [viewDoc, setViewDoc] = useState(null);
  const [scanTarget, setScanTarget] = useState(null);
  const [scanProcessing, setScanProcessing] = useState(false);
  const [showPassport, setShowPassport] = useState(
    !!(client?.passport || client?.passportPlace || client?.docPassport)
  );

  const parseMRAIDate = (mrzDate) => {
    if (!mrzDate || mrzDate.length < 6) return '';
    const yy = mrzDate.substring(0, 2);
    const mm = mrzDate.substring(2, 4);
    const dd = mrzDate.substring(4, 6);
    const currentYear = new Date().getFullYear() % 100;
    const century = parseInt(yy) > currentYear + 10 ? '19' : '20';
    return `${century}${yy}-${mm}-${dd}`;
  };

  const handleScanCapture = async (rawDataUrl) => {
    const field = scanTarget;
    setScanTarget(null);
    setScanProcessing(true);
    try {
      if (rawDataUrl && rawDataUrl.startsWith('data:')) {
        const url = await uploadBase64ToStorage(rawDataUrl, 'clients');
        setData(prev => ({ ...prev, [field]: url }));
      } else {
        setData(prev => ({ ...prev, [field]: rawDataUrl }));
      }
    } catch (e) {
      alert('❌ Erreur: ' + e.message);
    } finally {
      setScanProcessing(false);
    }
  };


  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert(t('file_too_large') || 'Le fichier est trop volumineux (max 5MB)');
      return;
    }
    uploadToStorage(file, 'clients')
      .then(url => setData(prev => ({ ...prev, [field]: url })))
      .catch(err => alert('❌ ' + err.message));
  };

  const submit = async () => {
    if (!data.name || !data.phone) return alert(t('name_phone_required'));
    
    const cleanData = { ...data };
    if (!showPassport) {
      cleanData.passport = '';
      cleanData.passportPlace = '';
      cleanData.docPassport = null;
    }

    const payload = {
      ...cleanData,
      creditBalance: parseFloat(cleanData.creditBalance) || 0
    };
    
    try {
      await onSave(payload);
      if (!client) {
        const formatTelegramDate = (dateStr) => {
          if (!dateStr) return '—';
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
          return dateStr;
        };

        await sendTelegramNotification(
          buildTelegramMessage('new_client', {
            name: payload.name, phone: payload.phone, cin: payload.cin,
            permis: payload.permis, email: payload.email, address: payload.address,
            nationality: payload.nationality,
            birthDate: formatTelegramDate(payload.birthDate),
            permisDate: formatTelegramDate(payload.permisDate),
            profession: payload.profession, phone2: payload.phone2,
            address2: payload.address2, notes: payload.notes
          }),
          'new_client'
        );
      }
      onClose();
    } catch (e) {
      alert('❌ Erreur lors de l\'enregistrement : ' + e.message);
    }
  };

  const renderUploadZone = (field, label, icon) => {
    const hasDoc = data[field];
    
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-3 flex flex-col items-center justify-center text-center hover:border-blue-400 transition bg-white">
        {hasDoc ? (
          <div className="w-full flex flex-col items-center gap-2">
            {hasDoc.startsWith('data:application/pdf') || hasDoc.includes('.pdf') ? (
              <div className="w-16 h-16 bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
                <FileText className="w-8 h-8" />
              </div>
            ) : (
              <img src={hasDoc} className="w-full h-20 object-cover rounded-lg shadow-sm border border-gray-200" alt="Document" />
            )}
            <div className="text-xs font-bold text-gray-700">{label}</div>
            <div className="flex gap-1.5 flex-wrap justify-center">
              <button 
                type="button"
                onClick={() => setViewDoc({ url: hasDoc, title: label })} 
                className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-bold rounded flex items-center gap-1 transition" 
              >
                <Eye className="w-3 h-3" />
                {isAr ? 'عرض' : 'Voir'}
              </button>
              <button 
                type="button"
                onClick={() => setData({ ...data, [field]: null })} 
                className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold rounded flex items-center gap-1 transition" 
              >
                <Trash2 className="w-3 h-3" />
                {isAr ? 'حذف' : 'Suppr.'}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full">
            {icon}
            <div className="text-sm font-semibold text-gray-700 mt-2 mb-3">{label}</div>
            <div className="flex flex-wrap gap-2 justify-center">
              <label className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1 transition">
                🖼️ {isAr ? 'معرض' : 'Galerie'}
                <input 
                  type="file" 
                  accept="image/*,application/pdf" 
                  className="hidden" 
                  onChange={(e) => handleFileChange(e, field)} 
                />
              </label>
              <button
                type="button"
                onClick={() => setScanTarget(field)}
                className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg flex items-center gap-1 transition shadow-sm"
              >
                📷 {isAr ? 'الكاميرا' : 'Caméra'}
              </button>
            </div>
            <div className="text-[10px] text-gray-400 mt-2">
              {isAr ? 'صورة أو PDF (5MB كحد أقصى)' : 'Image/PDF (5MB max)'}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal onClose={onClose} title={client ? t('edit_client') : t('new_client')}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t('full_name_req')} value={data.name} onChange={v => setData({ ...data, name: v })} />
          <Field label={t('phone_req')} value={data.phone} onChange={v => setData({ ...data, phone: v })} />
          <Field label={t('cin_label')} value={data.cin} onChange={v => setData({ ...data, cin: v })} />
          <Field label={t('permis_num_label')} value={data.permis} onChange={v => setData({ ...data, permis: v })} />
          <Field label={t('email_label')} value={data.email} onChange={v => setData({ ...data, email: v })} />
          <Field label={t('address_label')} value={data.address} onChange={v => setData({ ...data, address: v })} />
          <Field label={t('initial_credit')} type="number" value={data.creditBalance} onChange={v => setData({ ...data, creditBalance: v })} />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('notes_label')}</label>
          <textarea 
            value={data.notes || ''} 
            onChange={e => setData({ ...data, notes: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
            rows={2} 
          />
        </div>
        
        {/* CONTRACT INFO */}
        <div className="border-t border-gray-200 pt-5 mt-2">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-purple-500" />
            {isAr ? '📄 معلومات إضافية للعقد (اختيارية)' : '📄 Informations additionnelles pour le contrat (optionnel)'}
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            {isAr 
              ? 'هذه المعلومات تُملأ تلقائياً في عقد الكراء عند الطباعة'
              : 'Ces informations seront remplies automatiquement dans le contrat lors de l\'impression'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NationalityPicker 
              label={isAr ? '🌍 الجنسية' : '🌍 Nationalité'} 
              value={data.nationality || 'Marocaine'} 
              onChange={v => setData({ ...data, nationality: v })}
            />
            <Field 
              label={isAr ? 'تاريخ الإزدياد' : 'Date de naissance'} 
              type="date"
              value={data.birthDate || ''} 
              onChange={v => setData({ ...data, birthDate: v })}
              className="px-3 py-2 text-sm max-w-[220px]"
            />
            <Field 
              label={isAr ? 'المهنة' : 'Profession'} 
              value={data.profession || ''} 
              onChange={v => setData({ ...data, profession: v })}
              placeholder={isAr ? 'مثال: تاجر' : 'Ex: Commerçant'}
            />
            <Field 
              label={isAr ? 'تاريخ إصدار رخصة السياقة' : 'Date délivrance permis'} 
              type="date"
              value={data.permisDate || ''} 
              onChange={v => setData({ ...data, permisDate: v })}
              className="px-3 py-2 text-sm max-w-[220px]"
            />

            {/* Passport Toggle */}
            <div className="col-span-1 md:col-span-2 flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl my-1">
              <input
                id="showPassport"
                type="checkbox"
                checked={showPassport}
                onChange={e => setShowPassport(e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
              <label htmlFor="showPassport" className="text-sm font-bold text-gray-700 cursor-pointer select-none">
                {isAr ? '🌐 إظهار معلومات جواز السفر' : '🌐 Afficher les informations du passeport'}
              </label>
            </div>

            {showPassport && (
              <>
                <Field 
                  label={isAr ? 'رقم جواز السفر' : 'N° Passeport'} 
                  value={data.passport || ''} 
                  onChange={v => setData({ ...data, passport: v })}
                />
                <Field 
                  label={isAr ? 'مكان إصدار الجواز' : 'Délivré à (passeport)'} 
                  value={data.passportPlace || ''} 
                  onChange={v => setData({ ...data, passportPlace: v })}
                />
              </>
            )}

            <Field 
              label={isAr ? 'الهاتف الثابت' : 'Téléphone fixe'} 
              value={data.phone2 || ''} 
              onChange={v => setData({ ...data, phone2: v })}
              placeholder="05 28 ..."
            />
            <Field 
              label={isAr ? 'عنوان آخر' : 'Autre adresse'} 
              value={data.address2 || ''} 
              onChange={v => setData({ ...data, address2: v })}
            />
          </div>
        </div>
        
        {/* ATTACHMENTS */}
        <div className="border-t border-gray-200 pt-5 mt-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Upload className="w-4 h-4 text-blue-500" />
              {t('official_docs')}
            </h3>

          </div>
          <p className="text-xs text-gray-500 mb-4">
            {isAr 
              ? '💡 يمكنك تصوير الوثائق مباشرة بكاميرا الهاتف (الوجه + الخلف)'
              : '💡 Vous pouvez photographier directement avec la caméra du téléphone (recto + verso)'}
          </p>
          
          {/* CIN */}
          <div className="mb-5 p-4 bg-emerald-50/50 border border-emerald-200 rounded-xl">
            <h4 className="text-sm font-bold text-emerald-900 mb-3 flex items-center gap-2">
              <FileBadge className="w-4 h-4 text-emerald-600" />
              {isAr ? '🆔 بطاقة التعريف الوطنية (CIN)' : '🆔 Carte Nationale (CIN)'}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderUploadZone('docCniRecto', 
                isAr ? '↑ الوجه (Recto)' : '↑ Recto', 
                <FileBadge className="w-7 h-7 text-emerald-500 mx-auto" />
              )}
              {renderUploadZone('docCniVerso', 
                isAr ? '↓ الخلف (Verso)' : '↓ Verso', 
                <FileBadge className="w-7 h-7 text-emerald-500 mx-auto" />
              )}
            </div>
          </div>
          
          {/* PERMIS */}
          <div className="mb-5 p-4 bg-blue-50/50 border border-blue-200 rounded-xl">
            <h4 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
              <Car className="w-4 h-4 text-blue-600" />
              {isAr ? '🚘 رخصة السياقة' : '🚘 Permis de conduire'}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderUploadZone('docPermisRecto', 
                isAr ? '↑ الوجه (Recto)' : '↑ Recto', 
                <Car className="w-7 h-7 text-blue-500 mx-auto" />
              )}
              {renderUploadZone('docPermisVerso', 
                isAr ? '↓ ...' : '↓ Verso', 
                <Car className="w-7 h-7 text-blue-500 mx-auto" />
              )}
            </div>
          </div>
          
          {/* PASSPORT */}
          {showPassport && (
            <div className="p-4 bg-purple-50/50 border border-purple-200 rounded-xl">
              <h4 className="text-sm font-bold text-purple-900 mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-purple-600" />
                {isAr ? '🌐 جواز السفر (اختياري)' : '🌐 Passeport (optionnel)'}
              </h4>
              {renderUploadZone('docPassport', 
                isAr ? 'صفحة معلومات' : 'Page d\'informations', 
                <Globe className="w-7 h-7 text-purple-500 mx-auto" />
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex gap-3 mt-6 border-t border-gray-100 pt-4">
        <button 
          onClick={onClose} 
          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-bold hover:bg-gray-50 text-gray-700 transition"
        >
          {t('cancel_btn')}
        </button>
        <button 
          onClick={submit} 
          className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-md shadow-blue-500/20 transition"
        >
          {t('save_btn')}
        </button>
      </div>

      {viewDoc && (
        <DocumentViewerModal 
          url={viewDoc.url} 
          title={viewDoc.title} 
          onClose={() => setViewDoc(null)} 
        />
      )}

      {scanTarget && (
        <LiveCameraCapture
          onCapture={handleScanCapture}
          onCancel={() => setScanTarget(null)}
        />
      )}

      {scanProcessing && (
        <div className="fixed inset-0 bg-black/80 z-[10001] flex flex-col items-center justify-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-emerald-500" />
          <div className="color-white font-bold text-base text-white">
            {isAr ? 'جاري تحميل الصورة...' : 'Téléchargement de l\'image...'}
          </div>
        </div>
      )}


    </Modal>
  );
}
